/**
 * W11 — a poll's options inside its bubble: one row per option with its share bar, its vote count
 * and the caller's own mark, and the footer that says how many votes there are.
 *
 * The question is the message's body and is drawn by the bubble above this block, `dir="auto"` and
 * all; nothing here repeats it. The rules are `lib/polls.ts`'s — whether a row is a control, which
 * answers are mine, what the bars are made of — so no row re-derives them.
 *
 * Option text is user content inside a row that also carries chrome (the bar, the count, the check),
 * so it is a `<bdi>` (§7.2): an Arabic option stays one unit and starts where the row's layout put
 * it. `own` decides only the meta tone, the way the bubble's own meta row does.
 *
 * The rows *are* the message's answer while it is up, which is why they are buttons with 44px
 * targets — and why the caller wraps this block in `inert` while the thread is selecting (W4):
 * while a selection is on, a tap on a bubble is the selection's, and a vote must not slip out
 * through the capture handler that stops the media controls.
 */
import type { MessageView } from '@olmt/shared'
import { isolateAuto } from '../lib/bidi'
import { useT } from '../lib/i18n'
import { canVote, choseOption, myVote, voteTally } from '../lib/polls'
import { CheckIcon } from './Icons'

type PollBodyProps = {
  message: MessageView
  meId: string | null
  /** Whose bubble this is; the meta tone follows it, exactly like the bubble's own meta row. */
  own: boolean
  /** Vote on one option — or take the same choice back. The caller owns the rule and the failure. */
  onVote: (message: MessageView, optionIndex: number) => void
}

export function PollBody({ message, meId, own, onVote }: PollBodyProps) {
  const t = useT()
  const poll = message.poll
  // A `poll`-kind row can arrive without its payload (a forwarded copy: the api copies the kind,
  // not the options). There is nothing to draw, and the question above still reads as a sentence.
  if (poll === null) return null

  const votable = canVote(message)
  /**
   * The caller's own answer(s). A single-choice poll owns exactly one index (`myVote`), so every
   * row compares against it; a multiple-choice poll may hold several, so each row asks about itself.
   * One answer decides both the mark and the label's verb, so the two can never disagree.
   */
  const mine = myVote(message, meId)
  const { total, perOption } = voteTally(poll)

  return (
    <div className="mt-2">
      <ul className="flex flex-col gap-1.5">
        {poll.options.map((option) => {
          const count = perOption[option.index] ?? 0
          const chosen = poll.multiple ? choseOption(option, meId) : option.index === mine
          const share = total === 0 ? 0 : Math.round((count / total) * 100)
          const action = t(chosen ? 'poll.unvote' : 'poll.vote', { option: isolateAuto(option.text) })
          const votes = t(count === 1 ? 'poll.votesOne' : 'poll.votes', { count })
          return (
            <li key={option.index}>
              {/* Disabled — not hidden — when the vote cannot land (the message is still being sent
                  and has no server id yet), and the name carries the reason: a dead control that
                  explains nothing is worse than no control. The name is the whole row's text, so
                  every visible piece is `aria-hidden` inside and cannot be spoken twice. */}
              <button
                type="button"
                disabled={!votable}
                aria-label={votable ? `${action}, ${votes}` : `${action}, ${votes} — ${t('chat.sending')}`}
                onClick={() => onVote(message, option.index)}
                className={`press block w-full overflow-hidden rounded-md text-start ring-1 ring-inset transition-colors duration-quick enabled:hover:bg-black/30 disabled:cursor-not-allowed ${
                  chosen ? 'ring-accent/60' : own ? 'ring-bubble-out-meta/20' : 'ring-black/25'
                } ${own ? 'bg-black/20' : 'bg-black/25'}`}
              >
                <span aria-hidden="true" className="relative flex min-h-11 items-center gap-2 px-2.5 py-1">
                  {/* The share, as a fill from the row's inline start — logical, so it mirrors with
                      the chrome. It is the second channel of the tally beside the count's number, and
                      the mark of my own answer is the third (the check below, never the hue alone).
                      The width moves on a vote the way the upload bar does, over the app's one
                      quick duration: motion that answers an action, not decoration. */}
                  <span
                    className={`absolute inset-y-0 start-0 transition-[width] duration-quick ${
                      chosen ? 'bg-accent/35' : 'bg-accent/20'
                    }`}
                    style={{ width: `${share}%` }}
                  />
                  <span className="relative min-w-0 flex-1 text-ui break-words">
                    <bdi>{option.text}</bdi>
                  </span>
                  {chosen && (
                    <span className="relative flex size-4 shrink-0 items-center justify-center rounded-full bg-accent text-on-accent">
                      <CheckIcon size={12} />
                    </span>
                  )}
                  <span
                    className={`relative numeric text-meta ${
                      chosen ? 'font-semibold text-accent' : own ? 'text-bubble-out-meta' : 'text-muted'
                    }`}
                  >
                    {count}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      <p className={`mt-1.5 text-meta ${own ? 'text-bubble-out-meta' : 'text-muted'}`}>
        {t(total === 1 ? 'poll.votesOne' : 'poll.votes', { count: total })}
        {/* A poll that takes several answers says so where the tallies are read, not only in the
            create dialog that set it — a reader who was not there has no other way to know. */}
        {poll.multiple && ` · ${t('poll.multipleHint')}`}
      </p>
    </div>
  )
}
